import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../../api/users';
import { departmentsApi } from '../../api/departments';
import { publicApi } from '../../api/public';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { User, Briefcase, Building } from 'lucide-react';

const ProfileForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Локальний стан для миттєвого відображення змін
  const [currentUserData, setCurrentUserData] = useState({
    fullName: user?.fullName || '',
    position: user?.position || '',
    departmentId: user?.departmentId || '',
    departmentName: user?.departmentName || '',
    email: user?.email || '',
    profilePictureUrl: user?.profilePictureUrl || '',
    isUnionMember: user?.isUnionMember || false
  });
  
  // Оновлюємо локальні дані при зміні user
  useEffect(() => {
    if (user) {
      setCurrentUserData({
        fullName: user.fullName || '',
        position: user.position || '',
        departmentId: user.departmentId || '',
        departmentName: user.departmentName || '',
        email: user.email || '',
        profilePictureUrl: user.profilePictureUrl || '',
        isUnionMember: user.isUnionMember || false
      });
    }
  }, [user]);
  
  // Завантаження департаментів
  const { data: departments = [], isLoading: departmentsLoading, error: departmentsError } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        const res = await departmentsApi.getAllDepartments();
        
        if (res.data) {
          if (Array.isArray(res.data)) {
            return res.data;
          }
          if (res.data.data && Array.isArray(res.data.data)) {
            return res.data.data;
          }
          if (res.data.departments && Array.isArray(res.data.departments)) {
            return res.data.departments;
          }
        }
        
        return [];
      } catch (error) {
        try {
          const publicRes = await publicApi.getDepartments();
          if (publicRes.data && Array.isArray(publicRes.data)) {
            return publicRes.data;
          }
          return [];
        } catch (publicError) {
          throw new Error('Не вдалося завантажити список підрозділів');
        }
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  });
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      fullName: currentUserData.fullName,
      departmentId: currentUserData.departmentId,
      position: currentUserData.position
    }
  });
  
  useEffect(() => {
    reset({
      fullName: currentUserData.fullName,
      departmentId: currentUserData.departmentId,
      position: currentUserData.position
    });
  }, [currentUserData, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    
    try {
      const selectedDepartmentId = data.departmentId ? parseInt(data.departmentId, 10) : null;
      
      if (selectedDepartmentId) {
        const departmentExists = departments.find(dept => dept.id === selectedDepartmentId);
        if (!departmentExists) {
          toast.error('Вибраний підрозділ не існує.');
          return;
        }
      }
      
      const cleanedData = {
        fullName: data.fullName || '',
        departmentId: selectedDepartmentId,
        position: data.position || '',
        isUnionMember: currentUserData.isUnionMember
      };
      
      const response = await usersApi.updateCurrentUserProfile(cleanedData);
      
      // Перевірка успіху та МИТТЄВЕ оновлення інтерфейсу
      if (response.status === 200 || (response.data && response.data.success !== false)) {
        const selectedDepartment = departments.find(d => d.id === selectedDepartmentId);
        
        // МИТТЄВО оновлюємо локальні дані
        const updatedData = {
          ...currentUserData,
          fullName: cleanedData.fullName,
          position: cleanedData.position,
          departmentId: cleanedData.departmentId,
          departmentName: selectedDepartment ? selectedDepartment.name : ''
        };
        
        setCurrentUserData(updatedData);
        
        // Також оновлюємо localStorage для консистентності
        const updatedUser = { ...user, ...updatedData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        toast.success('Профіль успішно оновлено!');
      } else {
        throw new Error('Неуспішна відповідь сервера');
      }
      
    } catch (error) {
      if (error.response?.status === 200) {
        const selectedDepartmentId = data.departmentId ? parseInt(data.departmentId, 10) : null;
        const selectedDepartment = departments.find(d => d.id === selectedDepartmentId);
        
        // Навіть при помилці JS, оновлюємо дані
        const updatedData = {
          ...currentUserData,
          fullName: data.fullName || '',
          position: data.position || '',
          departmentId: selectedDepartmentId,
          departmentName: selectedDepartment ? selectedDepartment.name : ''
        };
        
        setCurrentUserData(updatedData);
        
        const updatedUser = { ...user, ...updatedData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        toast.success('Профіль оновлено!');
      } else {
        const errorMessage = error.response?.data?.error || 
                            error.response?.data?.message || 
                            error.message ||
                            'Помилка при оновленні профілю';
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-4">
        <h2 className="text-2xl font-semibold">Ваш профіль</h2>
      </div>
      <p className="text-gray-600 mb-6">Керуйте інформацією вашого профілю</p>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Блок інформації користувача - ОНОВЛЮЄТЬСЯ МИТТЄВО */}
        <div className="flex items-center mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="w-16 h-16 rounded-full overflow-hidden mr-4 border border-gray-200 flex-shrink-0">
            {currentUserData.profilePictureUrl ? (
              <img
                src={currentUserData.profilePictureUrl}
                alt={currentUserData.fullName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.parentNode.querySelector('.fallback-avatar');
                  if (fallback) {
                    fallback.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div 
              className="w-full h-full bg-blue-100 flex items-center justify-center fallback-avatar"
              style={{ display: currentUserData.profilePictureUrl ? 'none' : 'flex' }}
            >
              <User size={32} className="text-blue-500" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-medium">{currentUserData.fullName || 'Користувач'}</h3>
            <p className="text-gray-500">{currentUserData.email}</p>
            <div className="text-sm text-gray-600 mt-1">
              <p><strong>Підрозділ:</strong> {currentUserData.departmentName || 'Не вказано'}</p>
              <p><strong>Посада:</strong> {currentUserData.position || 'Не вказано'}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
              Прізвище, ім'я та по батькові
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User size={18} className="text-gray-400" />
              </div>
              <input
                id="fullName"
                type="text"
                className={`block w-full pl-10 py-3 text-gray-700 bg-white border ${
                  errors.fullName ? 'border-red-500' : 'border-gray-300'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Введіть ваше ПІБ"
                {...register('fullName', { required: true })}
              />
            </div>
            {errors.fullName && (
              <p className="mt-1 text-sm text-red-600">ПІБ є обов'язковим полем</p>
            )}
          </div>

          <div>
            <label htmlFor="departmentId" className="block text-sm font-medium text-gray-700 mb-1">
              Підрозділ
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building size={18} className="text-gray-400" />
              </div>
              <select
                id="departmentId"
                className="block w-full pl-10 py-3 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                {...register('departmentId')}
                disabled={departmentsLoading}
              >
                <option value="">
                  {departmentsLoading ? 'Завантаження...' : 'Виберіть підрозділ'}
                </option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            {departmentsError && departments.length === 0 && (
              <p className="mt-1 text-sm text-yellow-600">
                Увага: Не вдалося завантажити список підрозділів.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
              Посада
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Briefcase size={18} className="text-gray-400" />
              </div>
              <input
                id="position"
                type="text"
                className="block w-full pl-10 py-3 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Введіть вашу посаду"
                {...register('position')}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading || departmentsLoading}
            className="inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition duration-150"
          >
            {loading ? 'Збереження...' : 'Зберегти зміни'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;
